import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useRide } from '../contexts/RideContext';
import { handleApiError } from '../lib/api';
import { Truck, MapPin, Navigation, Loader2, AlertCircle, X } from 'lucide-react';
import { Agent } from '@ouagaliv/shared';

export default function AvailableRides() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const agentUser = user as Agent | null;
  // La position et le rafraîchissement périodique sont gérés en continu par Layout.
  const { availableRides, isLoading, acceptRide, declineRide } = useRide();
  const [accepting, setAccepting] = useState<string | null>(null);
  const [error, setError] = useState('');

  const handleAccept = async (rideId: string) => {
    setAccepting(rideId);
    setError('');
    try {
      await acceptRide(rideId);
      navigate(`/ride/${rideId}`);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setAccepting(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container pt-4 pb-24">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-semibold text-text">Courses disponibles</h1>
          <span className={`badge ${availableRides.length > 0 ? 'badge-primary' : 'badge-gray'}`}>
            {availableRides.length} course{availableRides.length > 1 ? 's' : ''}
          </span>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {!agentUser?.isAvailable ? (
          <div className="card text-center py-12">
            <Truck size={64} className="mx-auto text-text-secondary mb-4" />
            <h3 className="text-lg font-medium text-text mb-2">Vous êtes indisponible</h3>
            <p className="text-text-secondary">Activez votre disponibilité dans le menu ou votre profil pour recevoir des courses.</p>
          </div>
        ) : isLoading && availableRides.length === 0 ? (
          <div className="card text-center py-8">
            <Loader2 size={48} className="mx-auto text-primary animate-spin mb-3" />
            <p className="text-text-secondary">Recherche de courses...</p>
          </div>
        ) : availableRides.length === 0 ? (
          <div className="card text-center py-12">
            <Truck size={64} className="mx-auto text-text-secondary mb-4" />
            <h3 className="text-lg font-medium text-text mb-2">Aucune course disponible</h3>
            <p className="text-text-secondary mb-4">Activez votre disponibilité et restez dans une zone active pour recevoir des courses</p>
            <div className="space-y-2 text-sm text-text-secondary">
              <p>• Vérifiez que vous êtes "Disponible" dans le menu</p>
              <p>• Approchez-vous des zones commerciales (Ouaga 2000, Centre-ville, etc.)</p>
              <p>• Les courses apparaissent automatiquement</p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {availableRides.map(ride => (
              <Link key={ride.id} to={`/ride/${ride.id}`} className={`ride-card ${ride.distanceKm && ride.distanceKm < 1 ? 'urgent' : ''}`} style={{textDecoration: 'none'}}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="badge badge-primary">{ride.type}</span>
                      {ride.distanceKm && ride.distanceKm < 1 && <span className="badge badge-warning">Très proche</span>}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-secondary mb-1">
                      <MapPin size={14} />
                      <span className="truncate">{ride.pickupAddress}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-text-secondary">
                      <Navigation size={14} />
                      <span className="truncate">{ride.dropoffAddress}</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 ml-4">
                    <div className="text-right">
                      <p className="text-xl font-bold text-text">{ride.priceFcfa.toLocaleString()} FCFA</p>
                      <p className="text-xs text-text-secondary">{ride.agentEarningsFcfa.toLocaleString()} FCFA pour vous</p>
                    </div>
                    {accepting === ride.id ? (
                      <Loader2 size={20} className="animate-spin text-primary" />
                    ) : (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={e => { e.preventDefault(); declineRide(ride.id); }}
                          className="btn btn-outline whitespace-nowrap"
                          style={{fontSize: '14px', padding: '8px 12px'}}
                          aria-label="Refuser cette course"
                        >
                          <X size={16} />
                        </button>
                        <button
                          onClick={e => { e.preventDefault(); handleAccept(ride.id); }}
                          className="btn btn-primary whitespace-nowrap"
                          style={{fontSize: '14px', padding: '8px 16px'}}
                        >
                          Accepter
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}